import { SERVER, api } from "../scripts/api.js";

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

/*
The Assignments tab: a table of every platform assignment with, per row —
  • Create team version (individual rows) → POST /assignments/:id/team-version
    (a small dialog picks the feedback level; competitions default to none)
  • Delete (admin only) → DELETE /assignments/:id (explicit confirm)
Mirrors the classes.js / class-detail.js component pattern: a custom element with
a show() router hook, a <dialog> for the team-version pick, api() for the session
cookie, and the shared .cd-table / .class-dialog / .dialog-actions styles.
*/
class AppAssignments extends HTMLElement {
   connectedCallback() {
      this.rows = [];
      this.isAdmin = false;
      this.innerHTML = `
         <section id="assignments">
            <header class="classes-header">
               <h1>Assignments</h1>
            </header>

            <div class="assignments-actions">
               <button class="btn-secondary" id="btn-refresh-assignments">Refresh</button>
            </div>

            <p class="msg" id="assignments-msg"></p>
            <div id="assignments-table"></div>
         </section>

         <dialog class="class-dialog" id="team-version-dialog">
            <form method="dialog" id="team-version-form">
               <h2>Create team version</h2>
               <p class="tv-desc" id="tv-source-name"></p>
               <label>Feedback level
                  <select name="feedback_level" id="tv-feedback">
                     <option value="none" selected>None — competition (no per-answer feedback)</option>
                     <option value="correct_incorrect">Correct / incorrect</option>
                     <option value="full">Full feedback</option>
                  </select>
               </label>
               <p class="msg err" id="team-version-error"></p>
               <div class="dialog-actions">
                  <button type="submit" class="btn-primary" id="btn-create-team-version">Create</button>
                  <button type="button" class="btn-secondary" id="btn-cancel-team-version">Cancel</button>
               </div>
            </form>
         </dialog>

         <dialog class="class-dialog" id="rename-dialog">
            <form method="dialog" id="rename-form">
               <h2>Rename assignment</h2>
               <label>Title
                  <input name="title" id="rename-title" maxlength="255" required>
               </label>
               <p class="tv-desc" id="rename-note"></p>
               <p class="msg err" id="rename-error"></p>
               <div class="dialog-actions">
                  <button type="submit" class="btn-primary" id="btn-save-rename">Save</button>
                  <button type="button" class="btn-secondary" id="btn-cancel-rename">Cancel</button>
               </div>
            </form>
         </dialog>
      `;

      this.renameDialog = this.querySelector("#rename-dialog");
      this.querySelector("#btn-cancel-rename").addEventListener("click", () => this.renameDialog.close());
      this.querySelector("#rename-form").addEventListener("submit", (e) => this.onRename(e));

      this.dialog = this.querySelector("#team-version-dialog");
      this.form = this.querySelector("#team-version-form");
      this.querySelector("#btn-refresh-assignments").addEventListener("click", () => this.render());
      this.querySelector("#btn-cancel-team-version").addEventListener("click", () => this.dialog.close());
      this.form.addEventListener("submit", (e) => this.onCreateTeamVersion(e));

      // Event delegation on the table so it survives re-renders
      this.querySelector("#assignments-table").addEventListener("click", (e) => {
         const btn = e.target.closest("button[data-action]");
         if (!btn || btn.disabled) return;
         const id = Number(btn.dataset.id);
         if (btn.dataset.action === "team-version") this.openTeamVersionDialog(id);
         else if (btn.dataset.action === "rename") this.openRenameDialog(id);
         else if (btn.dataset.action === "delete") this.onDelete(id);
      });
   }

   show() {
      if (this.loaded) return;
      this.loaded = true;
      this.render();
   }

   setMsg(text, kind = "") {
      const el = this.querySelector("#assignments-msg");
      el.textContent = text;
      el.className = `msg${kind ? " " + kind : ""}`;
   }

   async render() {
      const table = this.querySelector("#assignments-table");
      table.innerHTML = `<p class="empty">Loading…</p>`;
      this.setMsg("");
      try {
         // role (for the admin-only Delete button) + the list, in parallel
         const [meRes, listRes] = await Promise.all([api("/me"), api("/assignments-list")]);
         const me = await meRes.json().catch(() => ({}));
         this.isAdmin = me && me.ok && me.role === "admin";
         const data = await listRes.json();
         if (!listRes.ok || !data.ok) throw new Error(data.reason || "failed");

         this.rows = data.assignments || [];
         if (!this.rows.length) {
            table.innerHTML = `<p class="empty">No assignments yet. Convert one to get started.</p>`;
            return;
         }
         this.renderTable();
      } catch (e) {
         table.innerHTML = "";
         this.setMsg("Couldn't load assignments.", "err");
      }
   }

   renderTable() {
      const table = this.querySelector("#assignments-table");
      const byId = new Map(this.rows.map((r) => [r.assignment_id, r]));
      table.innerHTML = `
         <table class="cd-table assignments-list">
            <thead>
               <tr>
                  <th>#</th><th>Title</th><th>Code</th><th>Questions</th>
                  <th>Type</th><th class="col-actions">Actions</th>
               </tr>
            </thead>
            <tbody>
               ${this.rows.map((a) => this.renderRow(a, byId)).join("")}
            </tbody>
         </table>
      `;
   }

   renderRow(a, byId) {
      const typeBadge = a.team_mode
         ? `<span class="badge badge-team">Team</span>`
         : `<span class="badge badge-individual">Individual</span>`;

      // source annotation for a team version
      let typeCell = typeBadge;
      if (a.team_mode && a.team_source_id != null) {
         const src = byId.get(a.team_source_id);
         typeCell += ` <span class="tv-note">from #${a.team_source_id}${src ? " " + esc(src.title) : ""}</span>`;
      }

      return `
         <tr data-id="${a.assignment_id}">
            <td class="mono">${a.assignment_id}</td>
            <td>${esc(a.title || "—")}</td>
            <td class="mono">${esc(a.assignment_code || "—")}</td>
            <td>${a.question_count}</td>
            <td>${typeCell}</td>
            <td class="col-actions">${this.actionsFor(a)}</td>
         </tr>
      `;
   }

   actionsFor(a) {
      const parts = [];

      // Create team version — individual rows only
      if (a.team_mode) {
         // it's already a team version; no create button
      } else if (a.team_version_id != null) {
         parts.push(`<span class="tv-note">has team version → #${a.team_version_id}</span>`);
      } else if (!a.has_file) {
         parts.push(`<button class="btn-mini" data-action="team-version" data-id="${a.assignment_id}" disabled
            title="No stored file — upload one to enable">Create team version</button>`);
      } else {
         parts.push(`<button class="btn-mini" data-action="team-version" data-id="${a.assignment_id}">Create team version</button>`);
      }

      // Rename. Available on every row, including team versions: their "(Team)"
      // title is not re-derived from the source, so editing it directly is the only
      // way to change it.
      parts.push(`<button class="btn-mini" data-action="rename" data-id="${a.assignment_id}">Edit</button>`);

      // Download (any row with a stored file)
      if (a.has_file) {
         parts.push(`<a class="btn-mini btn-link" href="${SERVER}/assignment/${a.assignment_id}/file">Download</a>`);
      }

      // Delete — admin only
      if (this.isAdmin) {
         parts.push(`<button class="btn-mini btn-danger" data-action="delete" data-id="${a.assignment_id}">Delete</button>`);
      }

      return parts.join(" ");
   }

   openRenameDialog(id) {
      const a = this.rows.find((r) => r.assignment_id === id);
      if (!a) return;
      this.renamingId = id;
      this.querySelector("#rename-error").textContent = "";
      const input = this.querySelector("#rename-title");
      input.value = a.title || "";
      // Say the one consequence that is not obvious from a title box: students get
      // the file named after the title, so a rename changes what they download next.
      this.querySelector("#rename-note").textContent = a.has_file
         ? "Students download this file named after its title, so renaming changes the filename they get."
         : "";
      this.renameDialog.showModal();
      input.select();
   }

   async onRename(e) {
      e.preventDefault();
      const id = this.renamingId;
      const btn = this.querySelector("#btn-save-rename");
      const errEl = this.querySelector("#rename-error");
      const title = this.querySelector("#rename-title").value.trim();
      errEl.textContent = "";
      if (!title) { errEl.textContent = "Give it a title."; return; }

      btn.disabled = true;
      btn.textContent = "Saving…";
      try {
         const res = await api(`/assignments/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title }),
         });
         const data = await res.json().catch(() => ({}));
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         this.renameDialog.close();
         await this.render();
         this.setMsg(`Renamed to "${data.title}".`, "ok");
      } catch (err) {
         errEl.textContent = err.message === "not_authorized"
            ? "You can only rename assignments in classes you teach."
            : "Couldn't rename that assignment.";
      } finally {
         btn.disabled = false;
         btn.textContent = "Save";
      }
   }

   openTeamVersionDialog(id) {
      const a = this.rows.find((r) => r.assignment_id === id);
      if (!a) return;
      this.pendingId = id;
      this.form.reset();
      this.querySelector("#tv-feedback").value = "none";
      this.querySelector("#team-version-error").textContent = "";
      this.querySelector("#tv-source-name").textContent =
         `Builds a team version of "${a.title || a.assignment_id}" (a separate assignment; same questions, graded as a team).`;
      this.dialog.showModal();
   }

   async onCreateTeamVersion(e) {
      e.preventDefault();
      const id = this.pendingId;
      const btn = this.querySelector("#btn-create-team-version");
      const errEl = this.querySelector("#team-version-error");
      const feedback = this.querySelector("#tv-feedback").value;
      errEl.textContent = "";
      btn.disabled = true;
      btn.textContent = "Creating…";
      try {
         const res = await api(`/assignments/${id}/team-version`, {
            method: "POST",
            body: JSON.stringify({ feedback_level: feedback }),
         });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            const reason = data.reason || "failed";
            const nice = {
               already_team: "That assignment is already a team version.",
               team_version_exists: "A team version already exists for this assignment.",
               no_stored_file: "This assignment has no stored file to build from.",
               converter_failed: "The converter service couldn't build the team file.",
            }[reason] || "Couldn't create the team version.";
            throw new Error(nice);
         }
         this.dialog.close();
         this.setMsg(`Created "${data.title}" (assignment #${data.assignment_id}, code ${data.code}).`, "ok");
         this.render();  // reload so the new team row + source annotation appear
      } catch (err) {
         errEl.textContent = err.message || "Couldn't create the team version.";
      } finally {
         btn.disabled = false;
         btn.textContent = "Create";
      }
   }

   async onDelete(id) {
      const a = this.rows.find((r) => r.assignment_id === id);
      const label = a ? `"${a.title || a.assignment_id}" (#${id})` : `#${id}`;
      if (!confirm(`Delete ${label}?\n\nThis permanently removes the assignment and ALL of its scores, submissions, and activity. This cannot be undone.`)) return;
      this.setMsg("");
      try {
         const res = await api(`/assignments/${id}`, { method: "DELETE" });
         const data = await res.json();
         if (!res.ok || !data.ok) {
            const nice = {
               not_authorized: "Only an admin can delete assignments.",
               not_found: "That assignment no longer exists.",
            }[data.reason] || "Couldn't delete the assignment.";
            throw new Error(nice);
         }
         this.setMsg(`Deleted "${data.title}" (#${data.deleted}).`, "ok");
         this.render();
      } catch (err) {
         this.setMsg(err.message || "Couldn't delete the assignment.", "err");
      }
   }
}

customElements.define("app-assignments", AppAssignments);
